import { isNumber, isString } from 'lodash'
import parsePhoneNumberFromString, { AsYouType, isPossiblePhoneNumber } from 'libphonenumber-js'

const DEFAULT_PHONE_REGION = 'US'

const parsePhoneNumber = (value, region = DEFAULT_PHONE_REGION) => {
  if (typeof parsePhoneNumberFromString !== 'function') {
    return null
  }

  try {
    return parsePhoneNumberFromString(value, region)
  } catch (error) {
    return null
  }
}

export const parsePhoneNumberToE164 = value => {
  if (value == null) {
    return null
  }

  const trimmed = String(value).trim()
  if (!trimmed) {
    return null
  }

  const phoneNumber = parsePhoneNumber(trimmed, DEFAULT_PHONE_REGION)
  if (phoneNumber?.isPossible()) {
    return phoneNumber.format('E.164')
  }

  return null
}

export const isValidE164PhoneNumber = value => {
  if (!value || typeof isPossiblePhoneNumber !== 'function') {
    return false
  }

  try {
    return isPossiblePhoneNumber(value)
  } catch (error) {
    return false
  }
}

export const formatPhoneNumberForDisplay = phone => {
  if (!isString(phone)) {
    return isNumber(phone) ? formatPhoneNumberForDisplay('' + phone) : null
  }

  const trimmed = phone.trim()
  if (!trimmed) {
    return null
  }

  const phoneNumber = parsePhoneNumber(trimmed, DEFAULT_PHONE_REGION)
  if (phoneNumber?.isPossible()) {
    return phoneNumber.formatInternational()
  }

  if (/^[-+()\d\s]+$/.test(trimmed)) {
    const rawPhone = trimmed.replace(/[^\d]+/g, '')
    if (rawPhone.length <= 10) {
      const areaCode = rawPhone.substr(0, 3)
      const prefix = rawPhone.substr(3, 3)
      const line = rawPhone.substr(6, 4)

      return `(${areaCode}) ${prefix}-${line}`
    } else if (rawPhone.length === 11) {
      const countryCode = rawPhone.charAt(0)
      const areaCode = rawPhone.substr(1, 3)
      const prefix = rawPhone.substr(4, 3)
      const line = rawPhone.substr(7, 4)

      return Number(countryCode) === 1
        ? `(${areaCode}) ${prefix}-${line}`
        : `${countryCode}-${areaCode}-${prefix}-${line}`
    } else {
      return `+${rawPhone}`
    }
  }

  return null
}

export const formatPhoneNumberForDatabase = value => {
  if (!value) {
    return null
  }

  const trimmed = String(value).trim()
  if (!trimmed) {
    return null
  }

  const e164 = parsePhoneNumberToE164(trimmed)
  if (e164) {
    return e164
  }

  return trimmed
}

const EMPTY_US_MASK_DIGITS = /^1?$/

export const normalizePhoneInputValue = value => {
  if (value == null) {
    return ''
  }

  const trimmed = String(value).trim()
  if (!trimmed) {
    return ''
  }

  const digits = trimmed.replace(/\D/g, '')
  if (!digits) {
    return ''
  }

  if (trimmed.startsWith('+1')) {
    const nationalDigits = trimmed.slice(2).replace(/\D/g, '')
    if (!nationalDigits) {
      return /^\+1\s*$/.test(trimmed) ? '+1' : ''
    }
  }

  if (EMPTY_US_MASK_DIGITS.test(digits)) {
    return ''
  }

  return trimmed
}

export const isUSPhoneInput = value => {
  if (value == null || String(value).trim() === '') {
    return false
  }

  return String(value).trim().startsWith('+1')
}

export const formatPhoneNumberAsYouType = (value, defaultCountry = DEFAULT_PHONE_REGION) => {
  if (value == null || String(value).trim() === '') {
    return ''
  }

  if (typeof AsYouType !== 'function') {
    return String(value)
  }

  try {
    return new AsYouType(defaultCountry).input(String(value))
  } catch (error) {
    return String(value)
  }
}

export const formatUsPhoneForMaskInput = value => {
  if (value == null || String(value).trim() === '') {
    return ''
  }

  const trimmed = String(value).trim()

  if (/^\+1 \(\d{3}\) \d{3}-\d{4}$/.test(trimmed)) {
    return trimmed
  }

  const phoneNumber = parsePhoneNumber(trimmed, DEFAULT_PHONE_REGION)
  if (phoneNumber?.country === 'US') {
    return `+1 ${phoneNumber.formatNational()}`
  }

  return trimmed
}
